import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";

import {postCheckout} from './helpers/checkout';
import {setCookie, getCookie} from './helpers/cookies';
import {postLog} from './helpers/log';

import MembershipHeaderActions from "./components/MembershipHeaderActions";
import MemberLogin from "./components/MemberLogin";
import MembershipLevelCard from "./components/MembershipLevelCard";
import MembershipActionCard from "./components/MembershipActionCard";
import Drawer from "./components/Drawer";
import MemberInfoForm from "./components/MemberInfoForm";
import DonationOptions from "./components/DonationOptions";
import MembershipPaymentForm from "./components/MembershipPaymentForm";
import RecaptchaComponent from "./components/RecaptchaComponent";
import buildDonationDetail from "./helpers/build-donation-detail";
import RoundUp, { useRoundUp } from "./components/RoundUp";


const Membership = (props) => {
    const {config} = props;
    const [acfData, setAcfData] = useState(null);
    const [authenticationToken, setAuthenticationToken] = useState('');
    const [activeScreen, setActiveScreen] = useState('levels');
    const [activeDashboardTab, setActiveDashboardTab] = useState('home');
    const [membershipLevels, setMembershipLevels] = useState(null); // used to store object of all membership levels from ACME
    const [giftCertificates, setGiftCertificates] = useState(null); // used to store object of all gift certificates from ACME
    const [intent, setIntent] = useState(null); // Purchase/MembershipRenewal/MembershipUpgrade etc.
    const [isMember, setIsMember] = useState(false);
    const [memberInfo, setMemberInfo] = useState(null); // used to store object of member information from ACME
    const [memberOptions, setMemberOptions] = useState(null); // used to store object of eligible upgrade options from ACME
    const [refetchingMemberData, setRefetchingMemberData] = useState(false); // used to trigger a refresh
    const [deliveryOption, setDeliveryOption] = useState('mail'); // the method of delivery for the gift certificate
    const [expirationStatus, setExpirationStatus] = useState(null); // active/expiring/expired
    const [selectedMembership, setSelectedMembership] = useState(null); // the chosen membership data, either for renewal or upgrade
    const [recipientDesignation, setRecipientDesignation] = useState('self'); // indicates the recipient of a membership (self/gift/certificate)
    const [cartTotal, setCartTotal] = useState('0.00');
    const [finalTotal, setFinalTotal] = useState('0.00');
    const [donationAmount, setDonationAmount] = useState('0.00');
    const [donationDetails, setDonationDetails] = useState({
        donorRecognitionType: 'self', 
        notes: '', 
        beneficiaryType: 'In honor of',
        beneficiaryCountry: 'US',
        valid: true,
        errors: {},
    });
    const { roundUpAmount, setRoundUpAmount, setShouldRoundUp, shouldRoundUp } = useRoundUp();
    const [hasPromoCode, setHasPromoCode] = useState(false);
    const [promoCode, setPromoCode] = useState(null);
    const [isCheckingPromoCode, setIsCheckingPromoCode] = useState(null);
    const [promoName, setPromoName] = useState(null);
    const [promoCodeStatus, setPromoCodeStatus] = useState(null); // null, valid, or invalid
    const [memberInfoFormObject, setMemberInfoFormObject] = useState({
        'giver': {
            'firstName': '',
            'lastName': '',
            'email': '',
            'note': ''
        },
        'primary': {
            'firstName': '',
            'lastName': '',
            'address1': '',
            'address2': '',
            'city': '',
            'state': '',
            'country': '',
            'zipCode': '',
            'email': '',
            'phoneNumber': ''
        },
        'secondary': {
            'firstName': '',
            'lastName': ''
        }
    });
    const [checkoutObject, setCheckoutObject] = useState({
        'shoppingCart': {
            'items': [],
            'couponCodes': [],
            'saleChannel': 'online'
        },
        'billingFirstName': '',
        'billingLastName': '',
        'billingAddress1': '',
        'billingAddress2': '',
        'billingCity': '',
        'billingState': '',
        'billingCountry': 'US',
        'billingZipCode': '',
        'billingEmail': '',
        'billingPhoneNumber': '',
        'manualEntryCardNumber': '',
        'expDate': '',
        'cvc': ''
    });
    const [passedRecaptchaV2, setPassedRecaptchaV2] = useState(false);
    const [isProcessingOrder, setIsProcessingOrder] = useState(false);
    const [processingError, setProcessingError] = useState('');
    const [orderNumber, setOrderNumber] = useState(null);
    const [orderAmount, setOrderAmount] = useState(null);
    const [membershipGiftCertificateIds, setMembershipGiftCertificates] = useState(null); // used to associate gift certificate and membership level data

    const clearPromoCode = () => {
        if(promoCode) {
            setPromoCode(null);
        }

        if(promoName) {
            setPromoName(null);
        }

        if(selectedMembership) {
            const selectedMembershipLevel = membershipLevels.find(lvl => lvl.name === selectedMembership.name);

            if(JSON.stringify(selectedMembershipLevel) !== JSON.stringify(selectedMembership)) {
                setMembershipLevels(JSON.parse(JSON.stringify(membershipLevels)));
                setSelectedMembership(membershipLevels.find(lvl => lvl.name === selectedMembership.name));
            }
        }
    };

    // Service fees apply only to ticket purchases, not memberships.
    const applicableServiceFees = [];
    const serviceFeeTotal = 0;

    // Retrieve ACF content
    useEffect(() => {
        const fetchACF = async () => {
            await fetch(`https://${config.domain}/wp-json/wp/v2/pages/${config.pageId}?acf_format=standard`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response);
                    }

                    return response.json();
                })
                .then(data => {
                    setAcfData(data);
                    let tempObj = {};
                    data.acf.gift_certificate_associations.forEach(entry => {
                        tempObj[entry.id] = entry.membership_category_id;
                    });
                    setMembershipGiftCertificates(tempObj); 
                    // console.log(data);
                })
                .catch(error => {
                    console.log(error);
                })
        };

        fetchACF();
    }, []);

    // Promo code checker
    const checkPromoCode = async (promoCode) => {
        setIsCheckingPromoCode(true);
        try {
            const response = await fetch(`https://${config.apiUrl}/api/coupons/check`, {
                method: 'POST',
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                },
                body: JSON.stringify({
                    "couponCode": promoCode,
                    "cart": getTempObj().shoppingCart
                })
            });
            if (!response.ok) {
                setIsCheckingPromoCode(false);
                postLog(config, authenticationToken, response.url, String(response.status));
                throw new Error(response);
            }
            const data = await response.json();
            console.log(data.data);
            if (data.data.coupon?.id == null) {
                setPromoCodeStatus('invalid');
                clearPromoCode();
            } else {
                setPromoCodeStatus('valid');
                setPromoName(data.data.coupon.name);

                if(data.data.cart?.items?.length) {
                    const newPrice = data.data.cart.items.reduce((total, item) => total + parseFloat(item.amount), 0).toFixed(2);
                    const newSelectedMembership = JSON.parse(JSON.stringify(selectedMembership));

                    newSelectedMembership.offerings[0].priceLists[0].prices[0].price = newPrice;
                    setSelectedMembership(newSelectedMembership);
                    setCartTotal(newPrice);
                }
            }
            setIsCheckingPromoCode(false);
        } catch (error) {
            console.error(error);
        }
    };

    // populate checkoutObject's shoppingCart item
    function getTempObj() {  
        let tempObj = {};  
        let items = [];
        let checkoutForms = [];

        // if is not signed in (ACME Purchase New Membership) and not buying a gift certificate
        if (!isMember && recipientDesignation != 'certificate') {
            items = [
                {
                    'quantity': 1,
                    'itemType': 'MembershipPurchase',
                    'membershipInfo':{
                        'membershipCategoryId': selectedMembership.id,
                        'membershipOfferingId': selectedMembership.offerings[0].id,
                        'pricePointId': selectedMembership.offerings[0].priceLists[0].prices[0].pricePoint.id,
                        'membershipCards':[{
                            'cardType': 'primary',
                            'city': memberInfoFormObject.primary.city,
                            'country': memberInfoFormObject.primary.country,
                            'email': memberInfoFormObject.primary.email,
                            'firstName': memberInfoFormObject.primary.firstName,
                            'lastName': memberInfoFormObject.primary.lastName,
                            'phoneNumber': memberInfoFormObject.primary.phoneNumber,
                            'state': memberInfoFormObject.primary.state,
                            'streetAddress1': memberInfoFormObject.primary.address1,
                            'streetAddress2': memberInfoFormObject.primary.address2 || '',
                            'zipCode': memberInfoFormObject.primary.zipCode
                        }],
                        'isGift': false
                    }
                }
            ];

            if (selectedMembership.rules.numberOfCardHolders > 1) {
                items[0].membershipInfo.membershipCards.push({
                    'cardType': 'secondary',
                    'firstName': memberInfoFormObject.secondary.firstName,
                    'lastName': memberInfoFormObject.secondary.lastName,
                    // address fields reuse information entered for primary
                    'city': memberInfoFormObject.primary.city,
                    'country': memberInfoFormObject.primary.country,
                    'state': memberInfoFormObject.primary.state,
                    'streetAddress1': memberInfoFormObject.primary.address1,
                    'streetAddress2': memberInfoFormObject.primary.address2 || '',
                    'zipCode': memberInfoFormObject.primary.zipCode
                });
            }

            if (recipientDesignation == 'gift') {
                items[0].membershipInfo.isGift = true;
                items[0].membershipInfo.gifterInfo = {
                    'customerSource': 'online',
                    'email': memberInfoFormObject.giver.email,
                    'firstName': memberInfoFormObject.giver.firstName,
                    'lastName': memberInfoFormObject.giver.lastName,
                    'version': 0,
                    'obfuscated': false
                }
                items[0].membershipInfo.notifyGiftRecipient = true;
                items[0].membershipInfo.giftMessage = memberInfoFormObject.giver.note;
                items[0].membershipInfo.auxiliary = false;
            }
        }

        // if not signed in and buying a gift certificate
        if (!isMember && recipientDesignation == 'certificate') {
            items.push({
                'itemType': 'Event',
                'eventId': giftCertificates.filter(entry => { return entry.membership_category_id == membershipGiftCertificateIds[selectedMembership.id]; })[0]?.event_id,
                'ticketingTypeId': membershipGiftCertificateIds[selectedMembership.id],
                'quantity': 1
            });

            // parse order_form fields and responses and map values from member info form
            let orderForm = JSON.parse(giftCertificates[0].order_form);

            let customFields = [];
            let fields = [];
            orderForm.forEach(item => {
                if (item.name != null && item.dataCustomField != null) {
                    switch (item.label) {
                        case 'Required': // the radio group for the mailing option
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': (deliveryOption == 'mail') ? 'MailtoPurchaser' : 'NoMailing'
                            });
                            fields.push({
                                'value': (deliveryOption == 'mail') ? 'MailtoPurchaser' : 'NoMailing',
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'First Name':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.firstName
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.firstName,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Last Name':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.lastName
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.lastName,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Email Address':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.email
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.email,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Street Address Line 1':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.address1
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.address1,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Street Address Line 2':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.address2
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.address2,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'City':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.city
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.city,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'State':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.state
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.state,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Zip Code':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.primary.zipCode
                            });
                            fields.push({
                                'value': memberInfoFormObject.primary.zipCode,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                        case 'Special note to include or additional information':
                            customFields.push({
                                'name': item.dataCustomField.split('%%')[2],
                                'value': memberInfoFormObject.giver.note
                            });
                            fields.push({
                                'value': memberInfoFormObject.giver.note,
                                'label': item.label,
                                'name': item.name,
                                'customFieldId': item.dataCustomField.split('%%')[0],
                                'customFieldLabel': item.dataCustomField.split('%%')[1],
                                'customFieldName': item.dataCustomField.split('%%')[2]
                            });
                            break;
                    }
                }
            });

            checkoutForms.push({
                'pricePointId': '', // unknown
                'itemId': '63613dbf02d2462c6681789c', // attempt same as eventId
                'itemType': 'Event',
                'responseForms': [{
                    'formId': '59ef4c47ca6afe40731d59fb',
                    'name': 'Membership Gift Certificate',
                    'type': 'order',
                    'source': 'acme',
                    'customFields': customFields,
                    'responseData': {
                        'fields': fields
                    }
                }]
            });

            tempObj['checkoutForms'] = checkoutForms;
        }

        // if is signed in (ACME Lifecycle Actions)
        if (isMember) {
            items = [
                {
                    'quantity': 1,
                    'itemType': intent, // MembershipRenewal, MembershipUpgrade, etc.
                    'membershipInfo':{
                        'membershipCategoryId': selectedMembership.id,
                        'membershipOfferingId': selectedMembership.offerings[0].id,
                        'pricePointId': selectedMembership.offerings[0].priceLists[0].prices[0].pricePoint.id,
                        'membershipId': memberInfo.primaryCardHolder.membershipId,
                        'membershipCards':[{
                            'constituentImportId': memberInfo.primaryCardHolder.constituentImportId,
                            'cardType': memberInfo.primaryCardHolder.cardType,
                            'city': memberInfo.address.city,
                            'country': memberInfo.address.country,
                            'email': memberInfo.primaryCardHolder.email,
                            'firstName': memberInfo.primaryCardHolder.firstName,
                            'lastName': memberInfo.primaryCardHolder.lastName,
                            'phoneNumber': memberInfo.address.phoneNumber,
                            'state': memberInfo.address.state,
                            'streetAddress1': memberInfo.address.streetAddress1,
                            'streetAddress2': memberInfo.address.streetAddress2 || '',
                            'zipCode': memberInfo.address.zipCode
                        }],
                        'isGift':false
                    }
                }
            ];

            if (selectedMembership.rules.numberOfCardHolders > 1) {
                items[0]['membershipInfo']['membershipCards'].push({
                    'cardType': 'secondary',
                    'firstName': memberInfoFormObject.secondary.firstName,
                    'lastName': memberInfoFormObject.secondary.lastName,
                    // address fields reuse information entered for primary
                    'city': memberInfoFormObject.primary.city,
                    'country': memberInfoFormObject.primary.country,
                    'state': memberInfoFormObject.primary.state,
                    'streetAddress1': memberInfoFormObject.primary.address1,
                    'streetAddress2': memberInfoFormObject.primary.address2 || '',
                    'zipCode': memberInfoFormObject.primary.zipCode
                });
            }
        }

        // add a donation item if needed
        if (donationAmount !== '0.00' || shouldRoundUp) {
            let finalDonationAmount = parseFloat(0);

            if (donationAmount !== '0.00') {
                finalDonationAmount += parseFloat(donationAmount);
            }

            if (shouldRoundUp) {
                finalDonationAmount += parseFloat(roundUpAmount);
            }

            items.push({
                'itemType': 'Inventory',
                'addonType': 'donation',
                'inventoryName': 'Donation',
                'inventoryId': 1703,
                'quantity': 1,
                'unitPrice': finalDonationAmount.toFixed(2),
                'amount': finalDonationAmount.toFixed(2),
                'donationDetail': buildDonationDetail(donationDetails),
            });
        }

        // add service fee items (guests only)
        for (const fee of applicableServiceFees) {
            items.push({
                'itemType': 'Inventory',
                'addonType': fee.addonType,
                'inventoryName': fee.inventoryName,
                'inventoryId': fee.inventoryId,
                'quantity': fee.quantity,
                'unitPrice': fee.unitPrice.toFixed(2),
                'amount': (fee.unitPrice * fee.quantity).toFixed(2),
            });
        }

        tempObj['shoppingCart'] = {
            items,
            couponCodes: (hasPromoCode) ? [promoCode] : [],
            saleChannel: 'online'
        };

        return tempObj;
    }

    // To submit the CORS checkout
    function placeOrder(token) {
        setProcessingError('');
        setIsProcessingOrder(true);

        const { shoppingCart : blankShoppingCart, ...checkoutObjectMain } = checkoutObject;
        const newCheckoutObject = { ...getTempObj(), ...checkoutObjectMain };
        console.log(newCheckoutObject);
        setCheckoutObject(newCheckoutObject);

        // send order to ACME
        async function checkout(cart) {
            var data = null;
            try {
                const response = await postCheckout(config, token, cart);

                data = await response.json();
                console.log(data);

                if (!response.ok) {
                    postLog(response.statusText, response.url, config);
                    throw new Error(response);
                }

                setIsProcessingOrder(false);
                setActiveScreen("confirmation");
                setOrderNumber(data.orderNumber);
                setOrderAmount(data.totalAmount);
                window.scrollTo(0, 200);

                // GA4 purchase event
                let mappedItems = data.orderItems.map((item) => {
                    return {
                        item_id: item.itemId,
                        item_name: typeof(item.displayName) === 'undefined' ? item.itemName : item.displayName,
                        quantity: item.quantity,
                        price: item.amount
                    };
                });

                if(typeof(window["gtag"]) !== "undefined") {
                    gtag('event', 'purchase', {
                        currency: 'USD',
                        transaction_id: data.orderNumber,
                        value: data.totalAmount,
                        coupon: hasPromoCode ? promoCode : '',
                        items: mappedItems
                    });
                }
            } catch (error) {
                setIsProcessingOrder(false);
                setProcessingError(data?.message || error.message || 'Processing error');
                console.error(error);
            }
        };

        checkout(newCheckoutObject);
    }

    // GA4 begin_checkout event
    useEffect(() => {
        if (activeScreen == 'selection') {
            if(typeof(window["gtag"]) !== "undefined") {
                gtag('event', 'begin_checkout', {
                    currency: 'USD',
                    value: cartTotal,
                    items: [
                        {
                            item_id: selectedMembership.id,
                            item_name: selectedMembership.name,
                            price: selectedMembership.offerings[0].priceLists[0].prices[0].price,
                            quantity: 1
                        }
                    ]
                });
            }
        }
    }, [activeScreen]);

    // MNTN Conversion Pixel
    useEffect(() => {
        if (activeScreen == 'confirmation') {
            console.log('mntn');
            var x=null,p,q,m,
            o="35832",
            l=orderNumber,
            i=orderAmount,
            c="",
            k="",
            g="",
            j="",
            u="",
            shadditional="";
            try{p=top.document.referer!==""?encodeURIComponent(top.document.referrer.substring(0,512)):""}catch(n){p=document.referrer!==null?document.referrer.toString().substring(0,512):""}try{q=window&&window.top&&document.location&&window.top.location===document.location?document.location:window&&window.top&&window.top.location&&""!==window.top.location?window.top.location:document.location}catch(b){q=document.location}try{m=parent.location.href!==""?encodeURIComponent(parent.location.href.toString().substring(0,512)):""}catch(z){try{m=q!==null?encodeURIComponent(q.toString().substring(0,512)):""}catch(h){m=""}}var A,y=document.createElement("script"),w=null,v=document.getElementsByTagName("script"),t=Number(v.length)-1,r=document.getElementsByTagName("script")[t];if(typeof A==="undefined"){A=Math.floor(Math.random()*100000000000000000)}w="dx.mountain.com/spx?conv=1&shaid="+o+"&tdr="+p+"&plh="+m+"&cb="+A+"&shoid="+l+"&shoamt="+i+"&shocur="+c+"&shopid="+k+"&shoq="+g+"&shoup="+j+"&shpil="+u+shadditional;y.type="text/javascript";y.src=("https:"===document.location.protocol?"https://":"http://")+w;r.parentNode.insertBefore(y,r)
        }
    }, [orderAmount]);

    // update cartTotal if gift certificate selection has a discount
    useEffect(() => {
        if (!selectedMembership) { return; }

        if (recipientDesignation == 'certificate' && giftCertificates != null && giftCertificates.length > 0 && membershipGiftCertificateIds[selectedMembership.id] != undefined) {
            setCartTotal(giftCertificates.filter(entry => { return entry.membership_category_id == membershipGiftCertificateIds[selectedMembership.id]; })[0]?.price.toFixed(2));
        } else {
            if (intent == 'MembershipUpgrade') {
                setCartTotal(parseFloat(selectedMembership.offerings[0].priceLists[0].prices[0].discountedPrice).toFixed(2));
            } else {
                setCartTotal(parseFloat(selectedMembership.offerings[0].priceLists[0].prices[0].price).toFixed(2));
            }
        }
    }, [recipientDesignation]);

    // authenticate to access SATAPI and save token for 1 year
    useEffect(() => {
        if (!getCookie('SATAPI_token')) {
            const authenticateApp = async () => {
                try {
                    const response = await fetch(`https://${config.apiUrl}/oauth/token`, {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            "grant_type": "client_credentials",
                            "client_id": (config.domain == 'www.seattleaquarium.org' || config.domain == 'seattleaquarium.org' || config.domain == 'seaaquariumweb.wpenginepowered.com') ? "3" : "1", // prod / dev
                            "client_secret": (config.domain == 'www.seattleaquarium.org' || config.domain == 'seattleaquarium.org' || config.domain == 'seaaquariumweb.wpenginepowered.com') ? "apFsFzcyBHGdUIUYPaAr8vHQCUa2GeUwnvQV0mVH" : "hOBRisrrRv9KWJrT2pD52e0JdgaXdl5tqDafnJSd", // prod / dev
                            // "client_id": "1", // dev
                            // "client_id": "3", // prod
                            // "client_secret": "hOBRisrrRv9KWJrT2pD52e0JdgaXdl5tqDafnJSd", // dev
                            // "client_secret": "apFsFzcyBHGdUIUYPaAr8vHQCUa2GeUwnvQV0mVH", // prod
                            "scope": "*"
                        })
                    });
                    if (!response.ok) {
                        throw new Error("Authentication error");
                    }
                    const token = await response.json();
                    setAuthenticationToken(token.access_token);
                    setCookie('SATAPI_token', token.access_token, 7); // disable cookie
                } catch (error) {
                    console.error(error);
                }
            };

            authenticateApp();
        } else {
            setAuthenticationToken(getCookie('SATAPI_token'));
        }
    }, []);

    // fetch membership levels data
    const fetchMembershipLevels = async () => {
        try {
            if (authenticationToken != '') {
                const response = await fetch(`https://${config.apiUrl}/api/membership/levels`, {
                    method: 'GET',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authenticationToken
                    },
                });
                if (!response.ok) {
                    postLog(config, authenticationToken, response.url, String(response.status));
                    throw new Error(response.url + ' ' + response.statusText);
                }
                const data = await response.json();
                console.log(data.data);
                setMembershipLevels(data.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchMembershipLevels();
    }, [authenticationToken]);

    // fetch gift certificates data
    useEffect(() => {
        if (authenticationToken != '') {
            const fetchGiftCertificates = async () => {
                try {
                    const response = await fetch(`https://${config.apiUrl}/api/gift-certificates`, {
                        method: 'GET',
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": authenticationToken
                        },
                    });
                    if (!response.ok) {
                        postLog(config, authenticationToken, response.url, String(response.status));
                        throw new Error(response.url + ' ' + response.statusText);
                    }
                    const data = await response.json();
                    console.log(data.data);
                    setGiftCertificates(data.data);
                } catch (error) {
                    console.error(error);
                }
            };

            fetchGiftCertificates();
        }
    }, [authenticationToken]);

    // skip to login screen if url queries have been passed
    useEffect(() => {
        const urlParameters = new URLSearchParams(window.location.search);
        const login = urlParameters.get('login');

        if (authenticationToken && login != null) {
            setActiveScreen('login');
            window.scrollTo(0, 200);
        }
    }, [authenticationToken]);

    // skip to selection summary if url queries have been passed
    useEffect(() => {
        const urlParameters = new URLSearchParams(window.location.search);
        const level = urlParameters.get('level');
        const recipient = urlParameters.get('recipient');

        if (authenticationToken && membershipLevels && level != null && recipient != null) {
            setRecipientDesignation(recipient.toLowerCase());

            membershipLevels.forEach(lvl => {
                if (lvl.name.toLowerCase().replaceAll(' ', '-').replaceAll("'", '') == level.toLowerCase()) {
                    setSelectedMembership(lvl);
                    setCartTotal(lvl.offerings[0].priceLists[0].prices[0].price);
                }
            });

            setIntent('Purchase');
            setActiveScreen('selection');
            window.scrollTo(0, 200);
        }
    }, [membershipLevels]);

    // determine expiration status of membership
    useEffect(() => {
        if (memberInfo != null) {
            let timeUntilExpiration = new Date(memberInfo.primaryCardHolder.membershipExpirationDate) - new Date();
            if (timeUntilExpiration >= 15552000000) {
                setExpirationStatus('active'); // over 6 months remaining
            } else if (timeUntilExpiration < 0) {
                setExpirationStatus('expired'); // already expired
            } else if (timeUntilExpiration < 15552000000) {
                setExpirationStatus('expiring'); // less than 6 months remaining
            }
        }
    }, [memberInfo]);

    // set fields in MemberInfoForm if a member
    useEffect(() => {
        if (memberInfo != null) {

            // country check for dropdown
            let country = memberInfo.address.country;
            if (country == 'United States' || country == 'United States of America' || country == 'USA') {
                country = 'US';
            } else if (country == 'Canada') {
                country = 'CA';
            }

            let tempObj = {
                'giver': {
                    'firstName': '',
                    'lastName': '',
                    'email': '',
                    'note': ''
                },
                'primary': {
                    'firstName': memberInfo.primaryCardHolder.firstName,
                    'lastName': memberInfo.primaryCardHolder.lastName,
                    'address1': memberInfo.address.streetAddress1,
                    'address2': '',
                    'city': memberInfo.address.city,
                    'state': memberInfo.address.state,
                    'country': country,
                    'zipCode': memberInfo.address.zipCode,
                    'email': memberInfo.primaryCardHolder.email,
                    'phoneNumber': memberInfo.primaryCardHolder.phoneNumber
                }
            }

            if (Object.values(memberInfo.secondaryCardHolders).length > 0 && Object.values(memberInfo.secondaryCardHolders)[0] != undefined) {
                if (Object.values(memberInfo.secondaryCardHolders)[0].name != null) {
                    tempObj['secondary'] = {
                        'firstName': Object.values(memberInfo.secondaryCardHolders)[0].name.split(' ')[0],
                        'lastName': Object.values(memberInfo.secondaryCardHolders)[0].name.split(' ')[1]
                    }
                } else if (Object.values(memberInfo.secondaryCardHolders)[0].firstName != null) {
                    tempObj['secondary'] = {
                        'firstName': Object.values(memberInfo.secondaryCardHolders)[0].firstName,
                        'lastName': Object.values(memberInfo.secondaryCardHolders)[0].lastName
                    }
                }
            } else {
                tempObj['secondary'] = {
                    'firstName': '',
                    'lastName': ''
                }
            }

            setMemberInfoFormObject(tempObj);
        }
    }, [memberInfo]);

    // calculate the amount needed to round up
    useEffect(() => {
        let centsRemainder = (parseFloat(cartTotal) + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2).split('.')[1];
        if (centsRemainder !== '00') {
            setRoundUpAmount(((100 - parseInt(centsRemainder)) / 100).toFixed(2));
        } else {
            setRoundUpAmount('0.00');
        }

        // set final total
        if (shouldRoundUp) {
            console.log('activeScreen, cartTotal, donationAmount, shouldRoundUp - setFinalTotal', ((parseFloat(cartTotal)) + parseFloat(donationAmount) + serviceFeeTotal + parseFloat(roundUpAmount)).toFixed(2));
            setFinalTotal(((parseFloat(cartTotal)) + parseFloat(donationAmount) + serviceFeeTotal + parseFloat(roundUpAmount)).toFixed(2));
        } else {
            console.log('activeScreen, cartTotal, donationAmount, shouldRoundUp - setFinalTotal', (parseFloat(cartTotal) + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2));
            setFinalTotal((parseFloat(cartTotal) + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2));
        }
    }, [activeScreen, cartTotal, donationAmount, shouldRoundUp, serviceFeeTotal]);

    // update finalCartTotal when selectedMembership, donationAmount, roundUpAmount, or serviceFeeTotal changes
    useEffect(() => {
        if (selectedMembership) {
            let total = 0;

            if (recipientDesignation == 'certificate' && giftCertificates != null && giftCertificates.length > 0 && membershipGiftCertificateIds[selectedMembership.id] != undefined) {
                total = ((giftCertificates.filter(entry => { return entry.membership_category_id == membershipGiftCertificateIds[selectedMembership.id]; })[0]?.price + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2));
            } else if (intent == 'MembershipUpgrade') {
                total = (parseFloat(selectedMembership.offerings[0].priceLists[0].prices[0].discountedPrice) + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2);
            } else {
                total = (parseFloat(selectedMembership.offerings[0].priceLists[0].prices[0].price) + parseFloat(donationAmount) + serviceFeeTotal).toFixed(2);
            }

            if (shouldRoundUp) {
                total = (parseFloat(total) + parseFloat(roundUpAmount)).toFixed(2);
            }

            console.log('selectedMembership, donationAmount, roundUpAmount - setFinalTotal', total);
            setFinalTotal(total);
        }
    }, [selectedMembership, donationAmount, roundUpAmount, serviceFeeTotal]);

    // clear selections when returning to dashboard or levels view
    useEffect(() => {
        if (activeScreen == 'dashboard' || activeScreen == 'levels') {
            setRecipientDesignation('self');
            setSelectedMembership(null);
            setCartTotal('0.00');
            console.log('activeScreen - setFinalTotal', '0.00');
            setFinalTotal('0.00');
            setShouldRoundUp(false);
            setRoundUpAmount('0.00');
            setPromoCode(null);
            setPromoCodeStatus(null);
        }
    }, [activeScreen]);

    // reset memberInfoFormObject if signing out
    useEffect(() => {
        if (activeScreen == 'levels') {
            setMemberInfoFormObject({
                'giver': {
                    'firstName': '',
                    'lastName': '',
                    'email': '',
                    'note': ''
                },
                'primary': {
                    'firstName': '',
                    'lastName': '',
                    'address1': '',
                    'address2': '',
                    'city': '',
                    'state': '',
                    'country': '',
                    'zipCode': '',
                    'email': '',
                    'phoneNumber': ''
                },
                'secondary': {
                    'firstName': '',
                    'lastName': ''
                }
            });
        }
    }, [activeScreen])

    const getMembershipLevel = (levelName) => {
        const level = membershipLevels.filter(level => level.name == name);

        return level ? level[0] : null;
    };

    const getMemberOptions = (optionType) => {
        const options = memberOptions?.eligible?.filter((option) => { return option.type === optionType });

        return options?.length ? options[0] : null;
    };

    const getTargetLevels = (optionType) => {
        return getMemberOptions(optionType)?.targetLevels;
    };

    return (
        <>
            <MembershipHeaderActions
                isMember={isMember}
                memberInfo={memberInfo}
                activeScreen={activeScreen}
                setActiveScreen={setActiveScreen}
                setIsMember={setIsMember}
                setRefetchingMemberData={setRefetchingMemberData}
            />

            {/* Membership Levels Screen */}
            <div
                class="membership-levels"
                style={{display: activeScreen === "levels" ? "block" : "none"}}
            >

                {!membershipLevels && <>
                    <div className="membership-levels__pending">
                        <i className="fa fa-spinner fa-pulse"></i>
                    </div>
                </>}

                {membershipLevels && <>
                    <h2>Membership Levels</h2>
                    <p><a href="#benefits">Learn More About Your Benefits</a></p>
                    <div className="membership__level-cards">
                        {membershipLevels && <>
                            <MembershipLevelCard
                                levelData={getMembershipLevel('Individual')}
                                split={true}
                                bottomData={getMembershipLevel('Individual Plus')}
                                setIntent={setIntent}
                                setSelectedMembership={setSelectedMembership}
                                setCartTotal={setCartTotal}
                                setActiveScreen={setActiveScreen}
                            />
                            <MembershipLevelCard
                                levelData={getMembershipLevel('Dual')}
                                split={true}
                                bottomData={getMembershipLevel('Dual Plus')}
                                setIntent={setIntent}
                                setSelectedMembership={setSelectedMembership}
                                setCartTotal={setCartTotal}
                                setActiveScreen={setActiveScreen}
                            />
                            <MembershipLevelCard
                                levelData={getMembershipLevel('Family')}
                                split={true}
                                bottomData={getMembershipLevel('Family Plus')}
                                setIntent={setIntent}
                                setSelectedMembership={setSelectedMembership}
                                setCartTotal={setCartTotal}
                                setActiveScreen={setActiveScreen}
                            />
                        </>}
                    </div>
                    <div>
                        {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.exclusive_levels_intro}}></div>}
                    </div>
                    <div class="membership__additional-levels">
                        {membershipLevels && <>
                            <Drawer title="Friends of the Aquarium">
                                {acfData && <p>{acfData.acf.friends_of_the_aquarium_intro}</p>}
                                {membershipLevels.map(level => {
                                    if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                        if (acfData && acfData.acf.levels_to_exclude.filter(lvl => lvl.id == level.id).length == 0) {
                                            return <MembershipLevelCard
                                                levelData={level}
                                                setIntent={setIntent}
                                                setSelectedMembership={setSelectedMembership}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                            />
                                        }
                                    }
                                })}
                            </Drawer>
                            <Drawer title="Aquarium Ambassadors">
                            {acfData && <p>{acfData.acf.aquarium_ambassadors_intro}</p>}
                                {membershipLevels.map(level => {
                                    if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                        if (acfData && acfData.acf.levels_to_exclude.filter(lvl => lvl.id == level.id).length == 0) {
                                            return <MembershipLevelCard
                                                levelData={level}
                                                setIntent={setIntent}
                                                setSelectedMembership={setSelectedMembership}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                            />
                                        }
                                    }
                                })}
                            </Drawer>
                        </>}
                    </div>
                    <br/>
                    <div id="benefits">
                        {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.general_membership_benefits}}></div>}
                    </div>
                </>}
            </div>

            {/* Membership Login */}
            <div
                className="membership-login"
                style={{display: activeScreen === "login" ? "block" : "none"}}
            >
                <h2>Member Login</h2>
                <MemberLogin
                    config={config}
                    authenticationToken={authenticationToken}
                    isMember={isMember}
                    memberInfo={memberInfo}
                    setIsMember={setIsMember}
                    setMemberInfo={setMemberInfo}
                    setMemberOptions={setMemberOptions}
                    checkoutObject={checkoutObject}
                    setCheckoutObject={setCheckoutObject}
                    setActiveScreen={setActiveScreen}
                    refetchingMemberData={refetchingMemberData}
                    setRefetchingMemberData={setRefetchingMemberData}
                />
            </div>

            {/* Membership Dashboard */}
            {memberInfo && <div
                className="membership-dashboard"
                style={{display: activeScreen === "dashboard" ? "block" : "none"}}
            >
                {expirationStatus != 'expired' && <div className="membership__reserve">
                    <a className="button button--stretch" target="_blank" href={`/tickets?id=${memberInfo.primaryCardHolder.acmeMembershipNumber}&lastName=${memberInfo.primaryCardHolder.lastName}`}>Reserve Tickets</a>
                    {acfData && <p>{acfData.acf.ticket_reservation_instructions}</p>}
                </div>}
                <nav className="membership__tabs">
                    <div
                        className={`membership__tabs-item ${activeDashboardTab === 'home' ? 'membership__tabs-item--active' : ''}`}
                        onClick={() => {
                            setActiveDashboardTab('home');
                            window.scrollTo(0, 200);
                        }}
                    >
                        My Membership
                    </div>
                    <div
                        className={`membership__tabs-item ${activeDashboardTab === 'exclusives' ? 'membership__tabs-item--active' : ''}`}
                        onClick={() => {
                            setActiveDashboardTab('exclusives');
                            window.scrollTo(0, 200);
                        }}
                    >
                        Member Exclusives
                    </div>
                </nav>
                <div
                    className="membership__home"
                    style={{display: activeDashboardTab === "home" ? "block" : "none"}}
                >
                    <h3>Hello {memberInfo.primaryCardHolder.firstName}</h3>
                    <p>Member ID: {memberInfo.primaryCardHolder.externalMembershipId}<br/>
                    Membership expire{expirationStatus == 'expired' ? 'd' : 's'} on {new Date(memberInfo.primaryCardHolder.membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}</p>
                    <h4>{memberInfo.primaryCardHolder.membershipCategory} Membership</h4>
                    {/friends|ambassador|president/i.test(memberInfo.primaryCardHolder.membershipCategory) && 
                        <p>Thank you for investing in the future of ocean conservation! View all your benefits in one place: <a href="/support-the-aquarium/current-donor-circle-members/">Current Donor Circle Members</a></p>
                    }
                    <Drawer title={expirationStatus != 'expired' ? 'View Current Benefits' : 'View Expired Benefits'}>
                        <ul style={{marginTop: 0}}>
                            {memberOptions?.current?.benefits?.map(benefit => {
                                return <li>{benefit.description}</li>;
                            })}
                        </ul>
                    </Drawer>

                    {/* expiration more than 6 months away */}
                    {expirationStatus == 'active' && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' && memberInfo.primaryCardHolder.category.search(/plus/i) == -1 &&
                        <div className="membership__expiration invert">
                            <h3>Upgrade to Plus</h3>
                            <p>Upgrade your membership and pay the difference between levels. Your expiration date will not change.</p>
                            <br/>
                            {getTargetLevels('upgrade')?.map(level => {
                                let regex = new RegExp(memberInfo.primaryCardHolder.membershipCategory);
                                if (level.name.search(regex) != -1) {
                                    return <button
                                        className="button"
                                        onClick={() => {
                                            setIntent('MembershipUpgrade');
                                            setSelectedMembership(level);
                                            setCartTotal(level.offerings[0].priceLists[0].prices[0].discountedPrice);
                                            setActiveScreen('selection');
                                            window.scrollTo(0, 200);
                                        }}
                                    >
                                        Upgrade Now
                                    </button>
                                }
                            })}
                        </div>
                    }

                    {/* expiring in 6 months message */}
                    {expirationStatus == 'expiring' && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' &&
                        <div className="membership__expiration invert">
                            <div className="membership__expiration-tag">Expiring Soon</div>
                            <p>Your membership expires on:</p>
                            <h3>{new Date(memberInfo.primaryCardHolder.membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}</h3>
                            {getTargetLevels('renew')?.map(level => {
                                return <button
                                    className="button"
                                    onClick={() => {
                                        setIntent('MembershipRenewal');
                                        setSelectedMembership(level);
                                        setCartTotal(level.offerings[0].priceLists[0].prices[0].price);
                                        setActiveScreen('selection');
                                        window.scrollTo(0, 200);
                                    }}
                                >
                                    Renew Now
                                </button>
                            })}
                        </div>
                    }

                    {/* expired message */}
                    {expirationStatus == 'expired' && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' &&
                        <div className="membership__expiration invert">
                            <div className="membership__expiration-tag">Expired</div>
                            <p>Your membership expired on:</p>
                            <h3>{new Date(memberInfo.primaryCardHolder.membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}</h3>
                            
                            {/* post term renewal option */}
                            {getTargetLevels('postTermRenew')?.map(level => {
                                return <button
                                    className="button"
                                    onClick={() => {
                                        setIntent('MembershipPostTermRenewal');
                                        setSelectedMembership(level);
                                        setCartTotal(level.offerings[0].priceLists[0].prices[0].price);
                                        setActiveScreen('selection');
                                        window.scrollTo(0, 200);
                                    }}
                                >
                                    Renew Now
                                </button>
                            })}

                            {/* rejoin option */}
                            {getTargetLevels('rejoin')?.map(level => {
                                return <button
                                    className="button"
                                    onClick={() => {
                                        setIntent('MembershipRejoin');
                                        setSelectedMembership(level);
                                        setCartTotal(level.offerings[0].priceLists[0].prices[0].price);
                                        setActiveScreen('selection');
                                        window.scrollTo(0, 200);
                                    }}
                                >
                                    Rejoin Now
                                </button>
                            })}
                            
                        </div>
                    }

                    <p>{memberInfo.primaryCardHolder.email}<br/>
                    {memberInfo.address.streetAddress1}<br/>
                    {memberInfo.address.city}, {memberInfo.address.state} {memberInfo.address.zipCode}<br/>
                    {memberInfo.address.phoneNumber}</p>
                    {Object.values(memberInfo.secondaryCardHolders).length > 0 && (Object.values(memberInfo.secondaryCardHolders)[0].firstName || Object.values(memberInfo.secondaryCardHolders)[0].name) && <>
                        <h5>Second Adult Member</h5>
                        {Object.values(memberInfo.secondaryCardHolders)[0].firstName && <>
                            <p>{Object.values(memberInfo.secondaryCardHolders)[0].firstName} {Object.values(memberInfo.secondaryCardHolders)[0].lastName}</p>
                        </>}
                        {!Object.values(memberInfo.secondaryCardHolders)[0].firstName && Object.values(memberInfo.secondaryCardHolders)[0].name && <>
                            <p>{Object.values(memberInfo.secondaryCardHolders)[0].name}</p>
                        </>}
                    </>}
                    {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.contact_instructions}}></div>}
                    {acfData && <a className="button" href={acfData.acf.faq_link.url} target={acfData.acf.faq_link.target} style={{'marginTop': 16}}>{acfData.acf.faq_link.title}</a>}

                    <div id="membership-change" className="membership__actions">
                        <h3>Manage Your Membership</h3>
                        {!memberOptions && <i className="fa fa-spinner fa-pulse"></i>}

                        {/* Renew Options */}
                        {getMemberOptions('renew') && <>
                            <Drawer title="Renew">
                                <p>Renew your membership for another year at the same level.</p>
                                {getTargetLevels('renew').map(level => {
                                    return <MembershipActionCard
                                        levelData={level}
                                        title={level.name}
                                        description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                        price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                        expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                        cta={'Renew'}
                                        setSelectedMembership={setSelectedMembership}
                                        intent={'MembershipRenewal'}
                                        setIntent={setIntent}
                                        setCartTotal={setCartTotal}
                                        setActiveScreen={setActiveScreen}
                                        order={level.displayOrder}
                                    />
                                })}
                            </Drawer>
                        </>}

                        {/* Upgrade Options */}
                        {getMemberOptions('upgrade') && memberInfo.primaryCardHolder.category != 'Staff/Volunteer' && <>
                            <Drawer title="Upgrade">
                                <p>Upgrade your membership and pay the difference between levels. Your expiration date will not change.</p>
                                <div className="action-card-wrap">
                                    {getTargetLevels('upgrade').map(level => {
                                        if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                            return <MembershipActionCard
                                                levelData={level}
                                                title={level.name}
                                                description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                price={parseInt(level.offerings[0].priceLists[0].prices[0].discountedPrice).toLocaleString('en-us')}
                                                expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                cta={'Upgrade'}
                                                setSelectedMembership={setSelectedMembership}
                                                intent={'MembershipUpgrade'}
                                                setIntent={setIntent}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                                order={level.displayOrder}
                                            />
                                        }
                                    })}
                                </div>

                                {getMemberOptions('upgrade') && <>
                                    {acfData && <div className="text-widget" dangerouslySetInnerHTML={{__html: acfData.acf.exclusive_levels_intro}}></div>}
                                    <Drawer title="Friends of the Aquarium">
                                        {acfData && <p>{acfData.acf.friends_of_the_aquarium_intro}</p>}
                                        <div className="action-card-wrap">
                                            {getTargetLevels('upgrade').map(level => {
                                                if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                    return <MembershipActionCard
                                                        levelData={level}
                                                        title={level.name}
                                                        description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                        price={parseInt(level.offerings[0].priceLists[0].prices[0].discountedPrice).toLocaleString('en-us')}
                                                        expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                        cta={'Upgrade'}
                                                        setSelectedMembership={setSelectedMembership}
                                                        intent={'MembershipUpgrade'}
                                                        setIntent={setIntent}
                                                        setCartTotal={setCartTotal}
                                                        setActiveScreen={setActiveScreen}
                                                        order={level.displayOrder}
                                                    />
                                                }
                                            })}
                                        </div>
                                    </Drawer>
                                </>}
                                {memberOptions.eligible.filter((option) => { return option.type === 'upgrade' }).length > 0 && <>
                                    <Drawer title="Aquarium Ambassadors">
                                        {acfData && <p>{acfData.acf.aquarium_ambassadors_intro}</p>}
                                        <div className="action-card-wrap">
                                            {memberOptions.eligible.filter((option) => { return option.type === 'upgrade' })[0].targetLevels.map(level => {
                                                if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                    return <MembershipActionCard
                                                        levelData={level}
                                                        title={level.name}
                                                        description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                        price={parseInt(level.offerings[0].priceLists[0].prices[0].discountedPrice).toLocaleString('en-us')}
                                                        expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                        cta={'Upgrade'}
                                                        setSelectedMembership={setSelectedMembership}
                                                        intent={'MembershipUpgrade'}
                                                        setIntent={setIntent}
                                                        setCartTotal={setCartTotal}
                                                        setActiveScreen={setActiveScreen}
                                                        order={level.displayOrder}
                                                    />
                                                }
                                            })}
                                        </div>
                                    </Drawer>
                                </>}
                            </Drawer>
                        </>}

                        {/* Renew & Downgrade/Upgrade Options */}
                        {expirationStatus == 'expiring' && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' && memberOptions && (memberOptions.eligible?.filter((option) => { return option.type === 'renewUpgrade' }).length > 0 || memberOptions.eligible?.filter((option) => { return option.type === 'renewDowngrade' }).length > 0) && <>
                            <Drawer title="Change &amp; Renew">
                                <div className="action-card-wrap">

                                    {memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' })[0].targetLevels.map(level => {
                                        if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                            return <MembershipActionCard
                                                levelData={level}
                                                title={level.name}
                                                description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                cta={'Renew & Change'}
                                                setSelectedMembership={setSelectedMembership}
                                                intent={'MembershipRenewalDowngrade'}
                                                setIntent={setIntent}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                                order={level.displayOrder}
                                            />
                                        }
                                    })}

                                    {memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' })[0].targetLevels.map(level => {
                                        if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                            return <MembershipActionCard
                                                levelData={level}
                                                title={level.name}
                                                description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                cta={'Renew & Upgrade'}
                                                setSelectedMembership={setSelectedMembership}
                                                intent={'MembershipRenewalUpgrade'}
                                                setIntent={setIntent}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                                order={level.displayOrder}
                                            />
                                        }
                                    })}

                                </div>

                                <h4>Exclusive Membership Levels</h4>
                                <p>Play a vital role in protecting our shared marine environment and the animals who call it home with your leadership support as a Friend of the Aquarium or an Aquarium Ambassador.</p>
                                <Drawer title="Friends of the Aquarium">
                                    {acfData && <p>{acfData.acf.friends_of_the_aquarium_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRenewalDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Upgrade'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRenewalUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                                <Drawer title="Aquarium Ambassadors">
                                    {acfData && <p>{acfData.acf.aquarium_ambassadors_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRenewalDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'renewUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Upgrade'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRenewalUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                            </Drawer>
                        </>}

                        {/* PostTerm Renew Options (not shown for Staff/Volunteer — they see downgrade options instead) */}
                        {getMemberOptions('postTermRenew') && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' && <>
                            <Drawer title="Renew">
                                <p>Renew your membership for another year at the same level.</p>
                                {getTargetLevels('postTermRenew').map(level => {
                                    return <MembershipActionCard
                                        levelData={level}
                                        title={level.name}
                                        description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                        price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                        expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                        cta={'Renew'}
                                        setSelectedMembership={setSelectedMembership}
                                        intent={'MembershipPostTermRenewal'}
                                        setIntent={setIntent}
                                        setCartTotal={setCartTotal}
                                        setActiveScreen={setActiveScreen}
                                        order={level.displayOrder}
                                    />
                                })}
                            </Drawer>
                        </>}

                        {/* PostTerm Renew & Downgrade/Upgrade Options */}
                        {expirationStatus == 'expired' && memberOptions && (memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' }).length > 0 || memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' }).length > 0) && <>
                            <Drawer title="Change &amp; Renew">
                                <div className="action-card-wrap">
                                
                                    {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' })[0].targetLevels.map(level => {
                                        if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                            return <MembershipActionCard
                                                levelData={level}
                                                title={level.name}
                                                description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                cta={'Renew & Change'}
                                                setSelectedMembership={setSelectedMembership}
                                                intent={'MembershipPostTermRenewalDowngrade'}
                                                setIntent={setIntent}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                                order={level.displayOrder}
                                            />
                                        }
                                    })}

                                    {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' })[0].targetLevels.map(level => {
                                        if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                            return <MembershipActionCard
                                                levelData={level}
                                                title={level.name}
                                                description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                cta={'Renew & Change'}
                                                setSelectedMembership={setSelectedMembership}
                                                intent={'MembershipPostTermRenewalUpgrade'}
                                                setIntent={setIntent}
                                                setCartTotal={setCartTotal}
                                                setActiveScreen={setActiveScreen}
                                                order={level.displayOrder}
                                            />
                                        }
                                    })}
                                </div>

                                <h4>Exclusive Membership Levels</h4>
                                <p>Play a vital role in protecting our shared marine environment and the animals who call it home with your leadership support as a Friend of the Aquarium or an Aquarium Ambassador.</p>
                                <Drawer title="Friends of the Aquarium">
                                    {acfData && <p>{acfData.acf.friends_of_the_aquarium_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipPostTermRenewalDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipPostTermRenewalUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                                <Drawer title="Aquarium Ambassadors">
                                    {acfData && <p>{acfData.acf.aquarium_ambassadors_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipPostTermRenewalDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'postTermRenewUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Renew & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipPostTermRenewalUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                            </Drawer>
                        </>}

                        {/* Rejoin Options */}
                        {getMemberOptions('rejoin') && <>
                            <Drawer title="Rejoin">
                                <p>Rejoin at your previous membership level.</p>
                                {getTargetLevels('rejoin').map(level => {
                                    return <MembershipActionCard
                                        levelData={level}
                                        title={level.name}
                                        description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                        price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                        expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                        cta={'Rejoin'}
                                        setSelectedMembership={setSelectedMembership}
                                        intent={'MembershipRejoin'}
                                        setIntent={setIntent}
                                        setCartTotal={setCartTotal}
                                        setActiveScreen={setActiveScreen}
                                        order={level.displayOrder}
                                    />
                                })}
                            </Drawer>
                        </>}

                        {/* Rejoin Downgrade/Upgrade Options */}
                        {expirationStatus == 'expired' && memberInfo.primaryCardHolder.membershipCategory != 'Staff/Volunteer' && memberOptions && (memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' }).length > 0 || memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' }).length > 0) && <>
                            <Drawer title="Change &amp; Rejoin">
                                <div className="action-card-wrap">
                                {memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' })[0].targetLevels.map(level => {
                                    if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                        return <MembershipActionCard
                                            levelData={level}
                                            title={level.name}
                                            description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                            price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                            expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                            cta={'Rejoin & Change'}
                                            setSelectedMembership={setSelectedMembership}
                                            intent={'MembershipRejoinDowngrade'}
                                            setIntent={setIntent}
                                            setCartTotal={setCartTotal}
                                            setActiveScreen={setActiveScreen}
                                            order={level.displayOrder}
                                        />
                                    }
                                })}

                                {memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' })[0].targetLevels.map(level => {
                                    if (parseInt(level.offerings[0].priceLists[0].prices[0].price) < 300) {
                                        return <MembershipActionCard
                                            levelData={level}
                                            title={level.name}
                                            description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                            price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                            expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                            cta={'Rejoin & Change'}
                                            setSelectedMembership={setSelectedMembership}
                                            intent={'MembershipRejoinUpgrade'}
                                            setIntent={setIntent}
                                            setCartTotal={setCartTotal}
                                            setActiveScreen={setActiveScreen}
                                            order={level.displayOrder}
                                        />
                                    }
                                })}
                                </div>

                                <h4>Exclusive Membership Levels</h4>
                                <p>Play a vital role in protecting our shared marine environment and the animals who call it home with your leadership support as a Friend of the Aquarium or an Aquarium Ambassador.</p>
                                <Drawer title="Friends of the Aquarium">
                                    {acfData && <p>{acfData.acf.friends_of_the_aquarium_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Rejoin & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRejoinDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 300 && parseInt(level.offerings[0].priceLists[0].prices[0].price) < 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Rejoin & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRejoinUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                                <Drawer title="Aquarium Ambassadors">
                                    {acfData && <p>{acfData.acf.aquarium_ambassadors_intro}</p>}
                                    <div className="action-card-wrap">
                                        {memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinDowngrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Rejoin & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRejoinDowngrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}

                                        {memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' }).length > 0 && memberOptions.eligible.filter((option) => { return option.type === 'rejoinUpgrade' })[0].targetLevels.map(level => {
                                            if (parseInt(level.offerings[0].priceLists[0].prices[0].price) >= 5000) {
                                                return <MembershipActionCard
                                                    levelData={level}
                                                    title={level.name}
                                                    description={level.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}
                                                    price={parseInt(level.offerings[0].priceLists[0].prices[0].price).toLocaleString('en-us')}
                                                    expiration={new Date(level.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}
                                                    cta={'Rejoin & Change'}
                                                    setSelectedMembership={setSelectedMembership}
                                                    intent={'MembershipRejoinUpgrade'}
                                                    setIntent={setIntent}
                                                    setCartTotal={setCartTotal}
                                                    setActiveScreen={setActiveScreen}
                                                    order={level.displayOrder}
                                                />
                                            }
                                        })}
                                    </div>
                                </Drawer>
                            </Drawer>
                        </>}

                    </div>
                </div>

                <div
                    className="membership__exclusives"
                    style={{display: activeDashboardTab === "exclusives" ? "block" : "none"}}
                >
                    {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.membership_exclusives}}></div>}
                </div>
            </div>}

            {/* Checkout Steps */}
            {['information', 'donation', 'payment', 'summary', 'confirmation'].includes(activeScreen) && <>
                <nav className="membership-checkout-steps">
                    <div className="fieldgroup fieldgroup--radio">
                        <input
                            type="radio"
                            id="information"
                            value="information"
                            name="step"
                            onClick={() => {
                                if(['donation', 'payment', 'summary'].includes(activeScreen)) {
                                    setActiveScreen('information');
                                    window.scrollTo(0, 200);
                                }
                            }}
                            checked={activeScreen === 'information'}
                            disabled={['donation', 'payment', 'summary'].includes(activeScreen) ? null : 'disabled'}
                        />
                        <label htmlFor="information">Information</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input
                            type="radio"
                            id="donate"
                            value="donate"
                            name="step"
                            onClick={() => {
                                if(['payment', 'summary'].includes(activeScreen)) {
                                    setActiveScreen('donation');
                                    window.scrollTo(0, 200);
                                }
                            }}
                            checked={activeScreen === 'donation'}
                            disabled={['payment', 'summary'].includes(activeScreen) ? null : 'disabled'}
                        />
                        <label htmlFor="donate">Donate</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input
                            type="radio"
                            id="payment"
                            value="payment"
                            name="step"
                            onClick={() => {
                                if(['summary', 'confirmation'].includes(activeScreen)) {
                                    setActiveScreen('payment');
                                    window.scrollTo(0, 200);
                                }
                            }}
                            checked={activeScreen === 'payment'}
                            disabled={['summary', 'confirmation'].includes(activeScreen) ? null : 'disabled'}
                        />
                        <label htmlFor="payment">Payment</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input
                            type="radio"
                            id="review"
                            value="review"
                            name="step"
                            onClick={(e) => e.preventDefault()}
                            checked={activeScreen === 'summary'}
                            disabled={['summary'].includes(activeScreen) ? null : 'disabled'}
                        />
                        <label htmlFor="review">Review</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input type="radio" id="done" value="done" name="step" onClick={(e) => e.preventDefault(e)} checked={activeScreen === 'confirmation'}/>
                        <label htmlFor="done">Done</label>
                    </div>
                </nav>
            </>}

            {/* Membership Selection */}
            <div
                className="membership-selection"
                style={{display: activeScreen === "selection" ? "block" : "none"}}
            >
                <h2>Review Your Selection</h2>

                {!isMember && selectedMembership && membershipGiftCertificateIds && <>
                    <h5>Who is this membership for?</h5>
                    <form className="membership-selection__designation">
                        <div className="fieldgroup fieldgroup--checkbox">
                            <input type="checkbox" id="self" checked={recipientDesignation == 'self'} onClick={() => setRecipientDesignation('self')}/>
                            <label htmlFor="self">This membership is for me.</label>
                        </div>
                        <div className="fieldgroup fieldgroup--checkbox">
                            <input type="checkbox" id="gift" checked={recipientDesignation == 'gift'} onClick={() => setRecipientDesignation('gift')}/>
                            <label htmlFor="gift">This membership is a gift membership (starts at purchase).</label>
                        </div>
                        {membershipGiftCertificateIds[selectedMembership.id] != undefined && <>
                            <div className="fieldgroup fieldgroup--checkbox">
                                <input type="checkbox" id="certificate" checked={recipientDesignation == 'certificate'} onClick={() => setRecipientDesignation('certificate')}/>
                                <label htmlFor="certificate">This membership is a gift certificate (starts when redeemed).</label>
                            </div>
                        </>}
                    </form>
                </>}

                {selectedMembership &&
                    <div className="level-summary invert">
                        <h4>
                            {intent == 'Purchase' && 'Purchase Membership'}
                            {(intent == 'MembershipRenewal' || intent == 'MembershipPostTermRenewal') && 'Renew Membership'}
                            {intent == 'MembershipRejoin' && 'Rejoin Membership'}
                            {intent == 'MembershipRejoinDowngrade' && 'Rejoin & Change Membership'}
                            {intent == 'MembershipRejoinUpgrade' && 'Rejoin & Upgrade Membership'}
                            {intent == 'MembershipUpgrade' && 'Upgrade Membership'}
                            {(intent == 'MembershipRenewalDowngrade' || intent == 'MembershipPostTermRenewalDowngrade') && 'Renew & Change Membership'}
                            {(intent == 'MembershipRenewalUpgrade' || intent == 'MembershipPostTermRenewalUpgrade') && 'Renew & Upgrade Membership'}
                        </h4>
                        <h3>{selectedMembership.name}</h3>
                        {recipientDesignation == 'certificate' && giftCertificates != null && giftCertificates.length > 0 && membershipGiftCertificateIds[selectedMembership.id] != undefined && <>
                            <p><b>${(giftCertificates.filter(entry => {return entry.membership_category_id == membershipGiftCertificateIds[selectedMembership.id]})[0]?.price).toFixed(2)}</b></p>
                        </>}
                        {(recipientDesignation != 'certificate' || membershipGiftCertificateIds[selectedMembership.id] == undefined) && <>
                            <p><b>${intent == 'MembershipUpgrade' ? selectedMembership.offerings[0].priceLists[0].prices[0].discountedPrice : selectedMembership.offerings[0].priceLists[0].prices[0].price}</b></p>
                        </>}
                        <p>{selectedMembership.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}</p>
                        {selectedMembership.offerings[0].membershipStartDate && <>
                            <p><b>Starts: </b>{new Date(selectedMembership.offerings[0].membershipStartDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}<br/>
                            <b>Ends: </b>{new Date(selectedMembership.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}</p>
                        </>}
                        <Drawer title="More Details">
                            <ul style={{marginTop: 0}}>
                                {selectedMembership.benefits.map(benefit => {
                                    return <li>{benefit.description}</li>;
                                })}
                            </ul>
                        </Drawer>
                    </div>
                }
                <hr/>
                <div className="membership__total">
                    <span className="membership__total-title">
                        Total
                    </span>
                    <span className="membership__total-amount">
                        ${cartTotal}
                    </span>
                </div>
                <button
                    className="button button--stretch"
                    onClick={() => {
                        setActiveScreen('information');
                        window.scrollTo(0, 200);
                    }}>Continue</button>
            </div>

            {/* Membership Information */}
            <div
                className="membership-selection"
                style={{display: activeScreen === "information" ? "block" : "none"}}
            >
                {recipientDesignation != 'certificate' && <h2>Membership Information</h2>}
                {selectedMembership && <MemberInfoForm
                    authenticationToken={authenticationToken}
                    config={config}
                    cartTotal={cartTotal}
                    selectedMembership={selectedMembership}
                    recipientDesignation={recipientDesignation}
                    memberInfoFormObject={memberInfoFormObject}
                    setMemberInfoFormObject={setMemberInfoFormObject}
                    deliveryOption={deliveryOption}
                    setDeliveryOption={setDeliveryOption}
                    activeScreen={activeScreen}
                    setActiveScreen={setActiveScreen}
                    intent={intent}
                />}
            </div>

            {/* Donation */}
            <div
                className="membership-donation"
                style={{display: activeScreen === "donation" ? "block" : "none"}}
            >
                <h2>Make a Donation</h2>
                <DonationOptions
                    acfData={acfData}
                    donationAmount={donationAmount}
                    setDonationAmount={setDonationAmount}
                    donationDetails={donationDetails}
                    setDonationDetails={setDonationDetails}
                />

                <hr/>
                <div className="membership__total">
                    <span className="membership__total-title">
                        Total
                    </span>
                    <span className="membership__total-amount">
                        ${finalTotal}
                    </span>
                </div>
                <button
                    className="button button--stretch"
                    disabled={!donationDetails.valid}
                    onClick={() => {
                        setActiveScreen('payment');
                        window.scrollTo(0, 200);
                    }}>Continue</button>
            </div>


            {/* Payment */}
            <div
                className="membership-donation"
                style={{display: activeScreen === "payment" ? "block" : "none"}}
            >
                <h2>Payment Information</h2>
                <MembershipPaymentForm
                    acfData={acfData}
                    finalTotal={finalTotal}
                    checkoutObject={checkoutObject}
                    setCheckoutObject={setCheckoutObject}
                    setActiveScreen={setActiveScreen}
                />
            </div>

            {/* Summary */}
            <div
                className="membership-summary"
                style={{display: activeScreen === "summary" ? "block" : "none"}}
            >
                <h2>Review Your Selection</h2>

                {selectedMembership &&
                    <div className="level-summary invert">
                        <h4>
                            {intent == 'Purchase' && 'Purchase Membership'}
                            {(intent == 'MembershipRenewal' || intent == 'MembershipPostTermRenewal') && 'Renew Membership'}
                            {intent == 'MembershipRejoin' && 'Rejoin Membership'}
                            {intent == 'MembershipRejoinDowngrade' && 'Rejoin & Change Membership'}
                            {intent == 'MembershipRejoinUpgrade' && 'Rejoin & Upgrade Membership'}
                            {intent == 'MembershipUpgrade' && 'Upgrade Membership'}
                            {(intent == 'MembershipRenewalDowngrade' || intent == 'MembershipPostTermRenewalDowngrade') && 'Renew & Change Membership'}
                            {(intent == 'MembershipRenewalUpgrade' || intent == 'MembershipPostTermRenewalUpgrade') && 'Renew & Upgrade Membership'}
                        </h4>
                        <h3>{selectedMembership.name}</h3>
                        {recipientDesignation == 'certificate' && giftCertificates != null && giftCertificates.length > 0 && membershipGiftCertificateIds[selectedMembership.id] != undefined && <>
                            <p><b>${(giftCertificates.filter(entry => {return entry.membership_category_id == membershipGiftCertificateIds[selectedMembership.id]})[0]?.price).toFixed(2)}</b></p>
                        </>}
                        {(recipientDesignation != 'certificate' || membershipGiftCertificateIds[selectedMembership.id] == undefined) && <>
                            <p><b>${intent == 'MembershipUpgrade' ? selectedMembership.offerings[0].priceLists[0].prices[0].discountedPrice : selectedMembership.offerings[0].priceLists[0].prices[0].price}</b></p>
                        </>}
                        <p>{selectedMembership.description.replace(/(<([^>]+)>)/gi, '').replace(/\&\S+\;/gi, '&')}</p>
                        {selectedMembership.offerings[0].membershipStartDate && <>
                            <p><b>Starts: </b>{new Date(selectedMembership.offerings[0].membershipStartDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}<br/>
                            <b>Ends: </b>{new Date(selectedMembership.offerings[0].membershipExpirationDate).toLocaleString('en-us', {'month': 'short', 'day': 'numeric', 'year': 'numeric'})}</p>
                        </>}
                        <Drawer title="More Details">
                            <ul style={{marginTop: 0}}>
                                {selectedMembership.benefits.map(benefit => {
                                    return <li>{benefit.description}</li>;
                                })}
                            </ul>
                        </Drawer>
                    </div>
                }

                {parseFloat(donationAmount) > 0 && <div className="ticket-summary__donation">
                    <h5 className="ticket-summary__heading">Donation<hr/></h5>
                    <ul className="ticket-summary__list">
                        <li>Chosen amount <span>${donationAmount}</span></li>
                    </ul>
                </div>}

                {serviceFeeTotal > 0 && <div className="ticket-summary__donation">
                    <h5 className="ticket-summary__heading">Service Fee<hr/></h5>
                    <ul className="ticket-summary__list">
                        {applicableServiceFees.map((fee, i) => (
                            <li key={i}>This fee helps our nonprofit aquarium cover payment processing costs, so more of your support goes toward our mission—thank you! <span>${(fee.unitPrice * fee.quantity).toFixed(2)}</span></li>
                        ))}
                    </ul>
                </div>}

                <div className="ticket-summary__promo">
                    <h5 className="ticket-summary__heading">Promo code<hr/></h5>
                    <div className="fieldgroup fieldgroup--checkbox">
                        <input type="checkbox" id="have-promo" checked={hasPromoCode} onClick={() => {
                            if(hasPromoCode) {
                                setHasPromoCode(false);
                                setPromoCodeStatus(null);
                                clearPromoCode();
                            } else {
                                setHasPromoCode(true);
                            }
                        }}/>
                        <label  htmlFor="have-promo">I have a promo code</label>
                    </div>
                    <div className="fieldgroup" style={{display: hasPromoCode ? 'block' : 'none'}}>
                        <label  htmlFor="promo-code">Promo code</label>
                        <input
                            className={`ticket-summary__promo-input ${(promoCodeStatus == 'invalid') ? 'ticket-summary__promo-input--error' : ''} ${(promoCodeStatus == 'valid') ? 'ticket-summary__promo-input--success' : ''}`}
                            type="text"
                            id="promo-code"
                            value={promoCode}
                            onChange={(e) => {
                                if(promoCodeStatus == 'valid') {
                                    setPromoCodeStatus(null);
                                    clearPromoCode();
                                }

                                setPromoCode(e.target.value);
                            }}/>
                        {promoCodeStatus == 'invalid' && <p className="ticket-summary__promo-message ticket-summary__promo-message--error">Invalid promo code.</p>}
                        {promoCodeStatus == 'valid' && <p className="ticket-summary__promo-message ticket-summary__promo-message--success">{promoName} applied.</p>}
                        {promoCodeStatus != 'valid' && <button
                            className="button"
                            onClick={() => checkPromoCode(promoCode)}
                            disabled={promoCode == null || promoCode.length < 1 || isCheckingPromoCode}
                        >
                            {!isCheckingPromoCode ? 'Apply' : <i className="fa fa-spinner fa-pulse"></i>}
                        </button>}
                    </div>
                </div>

                <RoundUp
                    donationAmount={donationAmount}
                    roundUpAmount={roundUpAmount}
                    setShouldRoundUp={setShouldRoundUp}
                    shouldRoundUp={shouldRoundUp}
                />

                <hr/>
                <div className="membership__total">
                    <span className="membership__total-title">
                        Total
                    </span>
                    <span className="membership__total-amount">
                        ${finalTotal}
                    </span>
                </div>
                <RecaptchaComponent setPassedRecaptchaV2={setPassedRecaptchaV2} v2SiteKey={config.recaptchaKeyV2} />
                {processingError && <p className="membership__error-text"><i className="fa fa-warning"></i>There was a problem placing your order: {processingError}. Please go back and try again. If the problem persists please <a href="https://seatleaquariun-tickets.shop/contact-us">contact us</a>.</p>}
                <button
                    className="button button--stretch"
                    onClick={() => {
                        window.grecaptcha.execute(config.recaptchaKeyV3, { action: 'submit' }).then((token) => {
                            placeOrder(token); // submit the checkoutObject to ACME
                        });
                    }}
                    disabled={!passedRecaptchaV2 || isProcessingOrder}
                >
                    {!isProcessingOrder ? 'Place Order' : <i className="fa fa-spinner fa-pulse"></i>}
                </button>
            </div>

            {/* Confirmation */}
            <div
                className="membership-confirmation"
                style={{display: activeScreen === "confirmation" ? "block" : "none"}}
            >
                <h5>{acfData && acfData.acf.confirmation_text}</h5>
                <p>Your order number is: {orderNumber}<br/>
                {checkoutObject.billingEmail}</p>
                {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.contact_text}}></div>}
                <hr/>
                <h5>Stay Up To Date</h5>
                <p>Get news and updates delivered to your inbox.</p>
                <a className="button" href="http://eepurl.com/h7FeBT" target="_blank">Join Our List</a>
                <hr/>
                <a className="button button--icon-left" href="/"><i className="fa fa-chevron-left"></i>Back to the Aquarium</a>
            </div>

        </>
    );
}

// ReactDOM.render(
//     <Membership />,
//     document.getElementById('membership')
// );

var App = function(config) {
    ReactDOM.render(
        <Membership config={config} />,
        document.getElementById('membership')
    );
}

window.App = App;